import React, { useState } from 'react';
import { Check, ArrowRight, Users, Clock, Package, Monitor, Building2, Lightbulb, Target, Sparkles, Video } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
type FormatType = 'inPerson' | 'digital';
export function ModernProductManagement() {
  const {
    t
  } = useLanguage();
  const [activeFormat, setActiveFormat] = useState<FormatType>('inPerson');
  const benefits = t('modernProductManagement.benefits') as string[];
  const forWhomItems = t('modernProductManagement.forWhomItems') as string[];
  const inPersonTiers = t('modernProductManagement.inPersonTiers') as {
    tier1: {
      name: string;
      duration: string;
      subtitle: string;
      description: string;
      features: string[];
    };
    tier2: {
      name: string;
      duration: string;
      subtitle: string;
      description: string;
      features: string[];
      highlighted?: boolean;
    };
    tier3: {
      name: string;
      duration: string;
      subtitle: string;
      description: string;
      features: string[];
    };
  };
  const digitalTiers = t('modernProductManagement.digitalTiers') as {
    tier1: {
      name: string;
      sessions: string;
      subtitle: string;
      description: string;
      features: string[];
    };
    tier2: {
      name: string;
      sessions: string;
      subtitle: string;
      description: string;
      features: string[];
      highlighted?: boolean;
    };
    tier3: {
      name: string;
      sessions: string;
      subtitle: string;
      description: string;
      features: string[];
    };
  };
  const formatToggle = t('modernProductManagement.formatToggle') as {
    inPerson: string;
    digital: string;
  };
  const forWhomIcons = [<Package className="text-amber-600 shrink-0" size={20} data-id="element-879" data-mp="jvbr66j87rbb" />, <Target className="text-amber-600 shrink-0" size={20} data-id="element-880" data-mp="et49r1fmnj1l" />, <Lightbulb className="text-amber-600 shrink-0" size={20} data-id="element-881" data-mp="trept2evd7ti" />, <Users className="text-amber-600 shrink-0" size={20} data-id="element-882" data-mp="tqn7gtaa96l6" />];
  const renderTierCard = (tier: {
    name: string;
    duration?: string;
    sessions?: string;
    subtitle: string;
    description: string;
    features: string[];
    highlighted?: boolean;
  }, index: number) => {
    const isHighlighted = tier.highlighted;
    return <Card key={index} className={`p-0 overflow-hidden flex flex-col h-full ${isHighlighted ? 'ring-2 ring-amber-500 shadow-2xl shadow-amber-100 scale-[1.02]' : 'border border-gray-200'}`} data-id="element-883" data-mp="assnjk7m9ao0">
        {isHighlighted && <div className="bg-amber-600 text-white text-center py-2 text-sm font-bold" data-id="element-884" data-mp="3efjb9t4zvgpx">
            <Sparkles className="inline-block mr-2" size={16} data-id="element-885" data-mp="39iapzjojuwkg" />
            {activeFormat === 'inPerson' ? 'Vinsælast' : 'Vinsælast'}
          </div>}
        <div className="p-8 flex flex-col flex-1" data-id="element-886" data-mp="1tri9cjkq7f5u">
          <div className="mb-6" data-id="element-887" data-mp="1ttbgs135y3ew">
            <span className="text-amber-600 font-bold text-sm uppercase tracking-wide" data-id="element-888" data-mp="1f2m0wlerc04p">
              {tier.name}
            </span>
            <h3 className="text-2xl font-bold text-charcoal mt-1 mb-1" data-id="element-889" data-mp="1k272l3lbaml4">
              {tier.subtitle}
            </h3>
            <div className="flex items-center gap-2 text-gray-500 text-sm" data-id="element-890" data-mp="27rt0lnpy6vrj">
              {activeFormat === 'inPerson' ? <>
                  <Clock size={16} className="text-amber-600" data-id="element-891" data-mp="1yquv10zv2g6u" />
                  <span data-id="element-892" data-mp="1yseawqhlup83">{tier.duration}</span>
                </> : <>
                  <Video size={16} className="text-amber-600" data-id="element-893" data-mp="22trjh8c316ip" />
                  <span data-id="element-894" data-mp="nadbu2l9ya25">{tier.sessions}</span>
                </>}
            </div>
          </div>

          <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-895" data-mp="reb5vyvqsuoc">
            {tier.description}
          </p>

          <ul className="space-y-3 mb-8 flex-1" data-id="element-896" data-mp="17l7f3m5mfntr">
            {tier.features.map((feature: string, i: number) => <li key={i} className="flex items-start gap-3" data-id="element-897" data-mp="12rcveiq0n8uo" data-mpi={i}>
                <div className="mt-0.5 p-1 rounded-full bg-amber-50 text-amber-600 flex-shrink-0" data-id="element-898" data-mp="xuutdqm2lom1">
                  <Check size={12} strokeWidth={3} data-id="element-899" data-mp="swl05ljwoy32" />
                </div>
                <span className="text-gray-700 text-sm" data-id="element-900" data-mp="1qj0jksvw85t9">{feature}</span>
              </li>)}
          </ul>

          <Link to="/contact" className="block mt-auto" data-id="element-901" data-mp="1lkyxdz367qdk">
            <Button className={`w-full ${isHighlighted ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-200' : ''}`} variant={isHighlighted ? 'primary' : 'outline'} rightIcon={<ArrowRight size={18} data-id="element-903" data-mp="555qt8tppgib" />} data-id="element-902" data-mp="1gkazctgiy99u">
              {t('modernProductManagement.inquiry')}
            </Button>
          </Link>
        </div>
      </Card>;
  };
  return <PageTransition data-id="element-904" data-mp="2cr5z2dmwb7ga">
      <div className="pt-12 pb-24" data-id="element-905" data-mp="2crpd3j9fdbp1">
        {/* Hero Section */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-906" data-mp="2cpcowod2q53j">
          <DecorativeShape type="blob" color="bg-amber-100" className="top-0 right-0 w-[500px] h-[500px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-907" data-mp="2gqoi13v5p8gh" />
          <DecorativeShape type="circle" color="bg-orange-100" className="bottom-0 left-0 w-64 h-64 -translate-x-1/4 translate-y-1/4 -z-10" data-id="element-908" data-mp="2qjvkafulo8cc" />

          <Animate className="text-center max-w-4xl mx-auto" data-id="element-909" data-mp="30f70l2lnpnm7">
            <span className="inline-block px-4 py-2 bg-amber-50 text-amber-600 font-medium rounded-full text-sm mb-6" data-id="element-910" data-mp="34i3nv6m567wh">
              {t('modernProductManagement.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-911" data-mp="1a4uah3qh628y">
              {t('modernProductManagement.title')} <br data-id="element-912" data-mp="1f2m4c3aojyfs" />
              <span className="text-amber-600" data-id="element-913" data-mp="1f46znv4tjlep">
                {t('modernProductManagement.titleHighlight')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-914" data-mp="2df86jty0vtud">
              {t('modernProductManagement.subtitle')}
            </p>

            <div className="flex flex-wrap justify-center gap-4 text-sm font-medium text-gray-500 mb-10" data-id="element-915" data-mp="2rfsnekkdnfl8">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-916" data-mp="2mf4v7vkewspf">
                <Clock size={18} className="text-amber-600" data-id="element-917" data-mp="31b2pf8arzmz0" />
                <span data-id="element-918" data-mp="2wd192v4qi1r8">{t('modernProductManagement.duration')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-919" data-mp="3a5qtiv4m6ym8">
                <Users size={18} className="text-amber-600" data-id="element-920" data-mp="3656vzqg4pszk" />
                <span data-id="element-921" data-mp="3k3ez6g3rdqj6">{t('modernProductManagement.audience')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-922" data-mp="3f4lpkyrlejoa">
                <Monitor size={18} className="text-amber-600" data-id="element-923" data-mp="2kg4jjbjczn7" />
                <span data-id="element-924" data-mp="2j55fwt1cg2f">{t('modernProductManagement.format')}</span>
              </div>
            </div>

            <Link to="/contact" data-id="element-925" data-mp="7hq25m92whm4">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white shadow-amber-200" size="lg" rightIcon={<ArrowRight size={20} data-id="element-927" data-mp="3ttdlq3hi5o8d" />} data-id="element-926" data-mp="3oszbcesfi276">
                {t('modernProductManagement.bookCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        {/* About Section */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-928" data-mp="361q1g4eqxbf2">
          <Animate data-id="element-929" data-mp="363j5rd1vzjjw">
            <h2 className="text-3xl font-bold mb-6 text-charcoal text-center" data-id="element-930" data-mp="3b0sueqhgbr6o">
              {t('modernProductManagement.aboutTitle')}
            </h2>
            <div className="prose prose-lg text-gray-600 text-center max-w-3xl mx-auto" data-id="element-931" data-mp="2x2b9t1mueusz">
              <p className="mb-4" data-id="element-932" data-mp="311k5ic1g12r6">{t('modernProductManagement.aboutP1')}</p>
              <p data-id="element-933" data-mp="3hiny5hievlcd">{t('modernProductManagement.aboutP2')}</p>
            </div>
          </Animate>
        </section>

        {/* What You Learn */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-934" data-mp="3cjkqdpb1r10p">
          <Animate data-id="element-935" data-mp="37ix39epxv4br">
            <h3 className="text-2xl font-bold mb-8 text-charcoal text-center" data-id="element-936" data-mp="37idjdsgqa5v3">
              {t('modernProductManagement.learnTitle')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-937" data-mp="2yj0ap20fhlbf">
              {benefits.map((benefit: string, index: number) => <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100" data-id="element-938" data-mp="2trrzc0fjfpvm" data-mpi={index}>
                  <div className="mt-0.5 p-1.5 rounded-full bg-amber-50 text-amber-600 flex-shrink-0" data-id="element-939" data-mp="2jv5gtiaopawr">
                    <Check size={14} strokeWidth={3} data-id="element-940" data-mp="2eu7vwxyai2rk" />
                  </div>
                  <span className="text-gray-700" data-id="element-941" data-mp="2at5wag0gnop2">{benefit}</span>
                </div>)}
            </div>
          </Animate>
        </section>

        {/* Format Toggle & Tiers */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-942" data-mp="ucytnqghrtfi">
          <Animate data-id="element-943" data-mp="uf36ixwdc2rx">
            {/* Format Toggle */}
            <div className="flex justify-center mb-12" data-id="element-944" data-mp="zdmrow351aj8">
              <div className="inline-flex bg-gray-100 rounded-full p-1.5" data-id="element-945" data-mp="14beljvncf6q2">
                <button onClick={() => setActiveFormat('inPerson')} className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all ${activeFormat === 'inPerson' ? 'bg-white text-amber-600 shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-946" data-mp="149bst3ut2zhn">
                  <Building2 size={18} data-id="element-947" data-mp="lcac4lrw3axv" />
                  {formatToggle.inPerson}
                </button>
                <button onClick={() => setActiveFormat('digital')} className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all ${activeFormat === 'digital' ? 'bg-white text-amber-600 shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-948" data-mp="3k2u39sirhhui">
                  <Monitor size={18} data-id="element-949" data-mp="23d2njxpj93gh" />
                  {formatToggle.digital}
                </button>
              </div>
            </div>

            {/* Tier Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch" data-id="element-950" data-mp="2dc06ml46w4s3">
              {activeFormat === 'inPerson' ? <>
                  {renderTierCard(inPersonTiers.tier1, 0)}
                  {renderTierCard(inPersonTiers.tier2, 1)}
                  {renderTierCard(inPersonTiers.tier3, 2)}
                </> : <>
                  {renderTierCard(digitalTiers.tier1, 0)}
                  {renderTierCard(digitalTiers.tier2, 1)}
                  {renderTierCard(digitalTiers.tier3, 2)}
                </>}
            </div>

            {/* Digital Note */}
            {activeFormat === 'digital' && <Animate delay={0.1} data-id="element-951" data-mp="3f74m2m3p2j7g">
                <div className="mt-8 bg-amber-50 rounded-2xl p-6 text-center max-w-3xl mx-auto" data-id="element-952" data-mp="3f6cynyk93rs9">
                  <Video className="mx-auto text-amber-600 mb-3" size={28} data-id="element-953" data-mp="3b6uifg3dk4vr" />
                  <p className="text-gray-700 leading-relaxed" data-id="element-954" data-mp="3666q8r3ethzy">
                    {t('modernProductManagement.digitalNote')}
                  </p>
                </div>
              </Animate>}
          </Animate>
        </section>

        {/* Sidebar-style section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-955" data-mp="2rhtunv88i15z">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-956" data-mp="2rfph0lkj9nsi">
            {/* For Whom */}
            <Animate data-id="element-957" data-mp="2ne481bb4dtnl">
              <Card className="p-8 bg-white shadow-xl border-t-4 border-amber-600 h-full" data-id="element-958" data-mp="2ih4353q0awy2">
                <h3 className="text-xl font-bold mb-6 text-charcoal" data-id="element-959" data-mp="2ifauxjzqw4ny">
                  {t('modernProductManagement.forWhom')}
                </h3>
                <ul className="space-y-4 mb-8" data-id="element-960" data-mp="28jzf0yclorem">
                  {forWhomItems.map((item: string, i: number) => <li key={i} className="flex items-start gap-3" data-id="element-961" data-mp="1ytr4fsxdgwmd" data-mpi={i}>
                      {forWhomIcons[i]}
                      <span className="text-gray-600" data-id="element-962" data-mp="23vq7t2t43tjb">{item}</span>
                    </li>)}
                </ul>
                <Link to="/contact" className="block" data-id="element-963" data-mp="1tw8z0cikczj4">
                  <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white shadow-amber-200" rightIcon={<ArrowRight size={18} data-id="element-965" data-mp="2mkwrot5n6r6p" />} data-id="element-964" data-mp="1twr5e1yxwu4r">
                    {t('modernProductManagement.inquiry')}
                  </Button>
                </Link>
              </Card>
            </Animate>

            {/* Group Offer */}
            <Animate delay={0.1} data-id="element-966" data-mp="2ctsf203fvif1">
              <div className="bg-amber-50 rounded-3xl p-8 text-center h-full flex flex-col justify-center" data-id="element-967" data-mp="2ct0gqhigj6l2">
                <div className="w-16 h-16 mx-auto bg-white rounded-full flex items-center justify-center shadow-md mb-4 text-amber-600" data-id="element-968" data-mp="30iusevr1ip3q">
                  <Users size={32} data-id="element-969" data-mp="tvnexmj0hrt2" />
                </div>
                <h4 className="font-bold text-xl mb-3" data-id="element-970" data-mp="tu2mq3k66kyd">
                  {t('modernProductManagement.groupTitle')}
                </h4>
                <p className="text-gray-600 mb-6" data-id="element-971" data-mp="13mi2cu7zuxg3">
                  {t('modernProductManagement.groupDesc')}
                </p>
                <Link to="/contact" className="text-amber-600 font-bold hover:underline text-lg" data-id="element-972" data-mp="ynnkpvlrjexk">
                  {t('modernProductManagement.contactLink')} →
                </Link>
              </div>
            </Animate>
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-973" data-mp="fwnqofzevexf">
          <Animate data-id="element-974" data-mp="fvcnoidsib7d">
            <Card className="p-12 bg-gradient-to-br from-amber-50 to-orange-50 border-0" data-id="element-975" data-mp="fz0jjo6pj9p9">
              <h2 className="text-3xl font-bold mb-4" data-id="element-976" data-mp="1wd0eekqp495n">
                {t('modernProductManagement.ctaTitle')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-977" data-mp="uz33nv4iscy">
                {t('modernProductManagement.ctaDesc')}
              </p>
              <Link to="/contact" data-id="element-978" data-mp="5ulh3zis0cmt">
                <Button className="bg-amber-600 hover:bg-amber-700 text-white shadow-amber-200" rightIcon={<ArrowRight size={18} data-id="element-980" data-mp="eu8fecg7l2bn" />} data-id="element-979" data-mp="5vn2oxxf4his">
                  {t('modernProductManagement.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}